/**
 * Shared bilingual primitives.
 *
 * Copied into each frontend by `scripts/sync.mjs`, like the rest of the brand kit — the
 * apps stay independently deployable, but they agree on what a locale is, which way the
 * page runs, and how a number is formatted.
 */

export const LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export interface LocaleMeta {
  code: Locale;
  /** How the language names itself — never translated. */
  nativeName: string;
  englishName: string;
  dir: 'ltr' | 'rtl';
  /** BCP-47 tag used for Intl formatting. */
  intl: string;
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: { code: 'en', nativeName: 'English', englishName: 'English', dir: 'ltr', intl: 'en-GB' },
  ar: { code: 'ar', nativeName: 'العربية', englishName: 'Arabic', dir: 'rtl', intl: 'ar-EG' },
};

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

export const dirFor = (locale: Locale): 'ltr' | 'rtl' => LOCALE_META[locale].dir;
export const isRtl = (locale: Locale): boolean => LOCALE_META[locale].dir === 'rtl';

/**
 * Pick a locale from an Accept-Language header.
 *
 * Deliberately simple: match the primary subtag only, so `ar-SA`, `ar-EG` and plain `ar`
 * all resolve to Arabic. Quality values are respected in order.
 */
export function negotiateLocale(acceptLanguage: string | null | undefined): Locale {
  if (!acceptLanguage) return DEFAULT_LOCALE;

  const ranked = acceptLanguage
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params.find((p) => p.trim().startsWith('q='));
      return { tag: tag.trim().toLowerCase(), q: q ? Number(q.split('=')[1]) || 0 : 1 };
    })
    .filter((entry) => entry.tag)
    .sort((a, b) => b.q - a.q);

  for (const { tag } of ranked) {
    const primary = tag.split('-')[0];
    if (isLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

/* ------------------------------------------------------------------ formatting */

/**
 * Numbers stay in Latin digits in both languages.
 *
 * Arabic-Indic digits (٠١٢٣) are correct for prose, but this is a business platform:
 * an invoice number, a SKU and a total have to be copy-pasteable into a spreadsheet or
 * an ERP, and searchable against records that store Latin digits. `latn` is enforced
 * rather than left to the locale default, which for `ar-EG` would otherwise vary.
 */
const NUMBER_OPTIONS = { numberingSystem: 'latn' } as const;

export function formatNumber(value: number, locale: Locale, options: Intl.NumberFormatOptions = {}): string {
  return new Intl.NumberFormat(LOCALE_META[locale].intl, { ...NUMBER_OPTIONS, ...options }).format(value);
}

export function formatMoney(
  value: number,
  locale: Locale,
  currency = 'EGP',
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(LOCALE_META[locale].intl, {
    ...NUMBER_OPTIONS,
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    ...options,
  }).format(value);
}

export function formatCompact(value: number, locale: Locale): string {
  return new Intl.NumberFormat(LOCALE_META[locale].intl, {
    ...NUMBER_OPTIONS,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatPercent(value: number, locale: Locale, fractionDigits = 1): string {
  return new Intl.NumberFormat(LOCALE_META[locale].intl, {
    ...NUMBER_OPTIONS,
    style: 'percent',
    maximumFractionDigits: fractionDigits,
  }).format(value / 100);
}

/**
 * Dates and times.
 *
 * Always rendered in the business timezone. The platform stores UTC everywhere and
 * displays Africa/Cairo (spec §29.24–25); a Cairo operator and a London one must read
 * the same wall-clock time for the same invoice.
 */
const TIMEZONE = 'Africa/Cairo';

export function formatDate(value: string | Date | null | undefined, locale: Locale): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE_META[locale].intl, {
    ...NUMBER_OPTIONS,
    timeZone: TIMEZONE,
    dateStyle: 'medium',
  }).format(date);
}

export function formatDateTime(value: string | Date | null | undefined, locale: Locale): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE_META[locale].intl, {
    ...NUMBER_OPTIONS,
    timeZone: TIMEZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/** "3 days ago" / "منذ ٣ أيام" — used for run history and notification lists. */
export function formatRelative(value: string | Date, locale: Locale): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(LOCALE_META[locale].intl, { numeric: 'auto' });

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000], ['month', 2_592_000], ['day', 86_400],
    ['hour', 3600], ['minute', 60], ['second', 1],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size || unit === 'second') {
      return formatter.format(Math.round(seconds / size), unit);
    }
  }
  return formatter.format(0, 'second');
}

/**
 * Interpolate `{name}` placeholders.
 *
 * Keeping the substitution outside the translated string means a translator moves the
 * placeholder to wherever the sentence needs it in Arabic, without the code caring about
 * word order.
 */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? String(values[key]) : match,
  );
}

/**
 * Arabic has six plural forms. Intl.PluralRules knows which one a count needs; this maps
 * that onto whichever forms a given string actually supplies, falling back sensibly so a
 * missing form degrades to the nearest available one rather than throwing.
 */
export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };

export function plural(count: number, forms: PluralForms, locale: Locale): string {
  const rule = new Intl.PluralRules(LOCALE_META[locale].intl).select(count);
  const chosen = forms[rule] ?? forms.other;
  return interpolate(chosen, { count: formatNumber(count, locale) });
}

/**
 * Pick the Arabic field of a record when the reader is Arabic, falling back to English.
 *
 * The Simulator API stores catalogue, branch and customer text in both languages as
 * sibling columns (`name`/`nameAr`). Rather than have every call site write the same
 * ternary — and get it subtly wrong when the Arabic value is an empty string rather than
 * null — this is the one place that decides.
 *
 * The fallback direction is deliberate and one-way: an untranslated row shows its English,
 * which is imperfect but readable. Falling back the other way would show Arabic to an
 * English reader, which is not.
 *
 *     localised(product, 'name', locale)   // product.nameAr ?? product.name
 */
export function localised<K extends string>(
  record: (Partial<Record<K, string | null>> & Partial<Record<`${K}Ar`, string | null>>) | null | undefined,
  field: K,
  locale: Locale,
): string {
  if (!record) return '';
  const english = record[field] ?? '';
  if (locale !== 'ar') return english;
  const arabic = (record as Record<string, string | null | undefined>)[`${field}Ar`];
  return arabic?.trim() ? arabic : english;
}

/** A person's display name in the reader's language, falling back to the English pair. */
export function localisedName(
  person: {
    firstName?: string | null; lastName?: string | null;
    firstNameAr?: string | null; lastNameAr?: string | null;
  } | null | undefined,
  locale: Locale,
): string {
  if (!person) return '';
  const english = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
  if (locale !== 'ar') return english;
  const arabic = `${person.firstNameAr ?? ''} ${person.lastNameAr ?? ''}`.trim();
  return arabic || english;
}

